import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { getClientWorkspaceCompany } from '../../../services/AnalysisApiClient';
import type { Analysis, ClientWorkspaceCompany } from '../../../services/AnalysisApiClient';
import type { CompanySummaryCardProps } from '../../shared/CompanySummaryCard';
import { useCompanyContext } from '../context/CompanyContext';
import {
  buildCompanySummaryCardProps,
  hasContextListGateForCompanyCard,
  pickDealContextAnalysis,
  pickDisplayAnalysisForCompanyCard,
  shouldShowClientCompanySummaryCard,
} from '../utils/clientWorkspaceCompanyCardModel';

export type UseClientWorkspaceCompanyCardResult = {
  workspaceCompany: ClientWorkspaceCompany | null;
  showDealContextCard: boolean;
  companySummaryProps: CompanySummaryCardProps;
  refreshWorkspaceCompany: () => Promise<ClientWorkspaceCompany | null>;
};

/**
 * Workspace company row + “deal context” analysis for the client shell header and any page that still needs the card props.
 * Pass `archivedAnalyses` from {@link CompanyContext} so the shell keeps context when there are no active analyses (archived-only).
 */
export function useClientWorkspaceCompanyCard(archivedForContext?: Analysis[]): UseClientWorkspaceCompanyCardResult {
  const { user } = useAuth();
  const { analyses } = useCompanyContext();
  const [workspaceCompany, setWorkspaceCompany] = useState<ClientWorkspaceCompany | null>(null);

  const includeArchivedFallback = archivedForContext !== undefined;

  const refreshWorkspaceCompany = useCallback(async () => {
    if (user?.loginType !== 'Client') {
      setWorkspaceCompany(null);
      return null;
    }
    try {
      const row = await getClientWorkspaceCompany();
      setWorkspaceCompany(row);
      return row;
    } catch {
      setWorkspaceCompany(null);
      return null;
    }
  }, [user?.loginType]);

  useEffect(() => {
    void refreshWorkspaceCompany();
  }, [refreshWorkspaceCompany]);

  const dealContextAnalysis = useMemo(() => pickDealContextAnalysis(analyses), [analyses]);

  const displayAnalysis = useMemo(
    () => pickDisplayAnalysisForCompanyCard(analyses, dealContextAnalysis, archivedForContext, includeArchivedFallback),
    [analyses, dealContextAnalysis, archivedForContext, includeArchivedFallback]
  );

  const hasContextListGate = hasContextListGateForCompanyCard(
    analyses.length,
    archivedForContext?.length,
    includeArchivedFallback
  );

  const showDealContextCard = useMemo(
    () =>
      shouldShowClientCompanySummaryCard({
        displayAnalysis,
        loginType: user?.loginType,
        hasContextListGate,
        workspaceCompanyName: String(workspaceCompany?.companyName || ''),
        analysisCompanyName: String(displayAnalysis?.data?.companyName || ''),
        userCompanyName: String(user?.companyName || ''),
      }),
    [displayAnalysis, user?.loginType, user?.companyName, hasContextListGate, workspaceCompany]
  );

  const companySummaryProps = useMemo(
    () => buildCompanySummaryCardProps(workspaceCompany, displayAnalysis, user?.companyName),
    [workspaceCompany, displayAnalysis, user?.companyName]
  );

  return { workspaceCompany, showDealContextCard, companySummaryProps, refreshWorkspaceCompany };
}
